import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { Menu, X, Home, ChevronDown, ChevronRight, User, LogOut, Heart, Calendar, Settings, Compass, Map, Search, Globe, Users, Swords, ScrollText } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../contexts/TranslationContext';
import { featureVisibility } from '../config/featureVisibility';
import { organizationNavCategories } from '../config/organizationNavCategories';
import { exploreNavItems, weaponsNavItem } from '../config/exploreNavItems';
import { weaponNavCategories } from '../config/weaponNavCategories';
import { toolsNavItems, optionalToolsNavItems } from '../config/toolsNavItems';
import LanguageSelector from './LanguageSelector';
import AuthModal from './Auth/AuthModal';

const GADKOT_LOGO = `${process.env.PUBLIC_URL || ''}/images/gadkot_logo.png`;

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobileWeaponsOpen, setIsMobileWeaponsOpen] = useState(false);
  const [isExploreDropdownOpen, setIsExploreDropdownOpen] = useState(false);
  const [isWeaponsSubmenuOpen, setIsWeaponsSubmenuOpen] = useState(false);
  const [isToolsDropdownOpen, setIsToolsDropdownOpen] = useState(false);
  const [isOrganizationsDropdownOpen, setIsOrganizationsDropdownOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [isGuestMenuOpen, setIsGuestMenuOpen] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, logout, isAuthenticated, isAdmin, isOrgRep } = useAuth();
  const adminUser = typeof isAdmin === 'function' ? isAdmin() : Boolean(isAdmin);
  const orgRepUser = typeof isOrgRep === 'function' ? isOrgRep() : Boolean(isOrgRep);
  const { t } = useTranslation();

  // Open sign-in when redirected from protected routes (e.g. admin)
  useEffect(() => {
    if (searchParams.get('signin') !== '1') return;
    setShowAuthModal(true);
    const next = new URLSearchParams(searchParams);
    next.delete('signin');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!isExploreDropdownOpen) {
      setIsWeaponsSubmenuOpen(false);
    } else if (location.pathname.startsWith('/weapons')) {
      setIsWeaponsSubmenuOpen(true);
    }
  }, [isExploreDropdownOpen, location.pathname]);

  const closeExploreMenus = () => {
    setIsExploreDropdownOpen(false);
    setIsWeaponsSubmenuOpen(false);
  };

  const exploreDropdownRef = useRef(null);
  const toolsDropdownRef = useRef(null);
  const organizationsDropdownRef = useRef(null);
  const userDropdownRef = useRef(null);
  const guestMenuRef = useRef(null);

  const isActive = (path) => location.pathname === path;

  const isExploreActive = () =>
    exploreNavItems.some((item) =>
      item.matchPrefix
        ? location.pathname === item.to || location.pathname.startsWith(item.matchPrefix)
        : isActive(item.to)
    ) || location.pathname.startsWith('/weapons');

  const isOrganizationsActive = () =>
    location.pathname === '/organizations' || location.pathname.startsWith('/organizations/');

  const navItemClass = (active) =>
    `gadkot-nav__link font-marathi ${active ? 'gadkot-nav__link--active' : ''}`;

  const mobileLinkClass = (active, nested = false) =>
    `gadkot-nav__mobile-link ${nested ? 'gadkot-nav__mobile-link--nested' : ''} ${
      active ? 'gadkot-nav__mobile-link--active' : ''
    }`;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exploreDropdownRef.current && !exploreDropdownRef.current.contains(event.target)) {
        setIsExploreDropdownOpen(false);
      }
      if (toolsDropdownRef.current && !toolsDropdownRef.current.contains(event.target)) {
        setIsToolsDropdownOpen(false);
      }
      if (organizationsDropdownRef.current && !organizationsDropdownRef.current.contains(event.target)) {
        setIsOrganizationsDropdownOpen(false);
      }
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setIsUserDropdownOpen(false);
      }
      if (guestMenuRef.current && !guestMenuRef.current.contains(event.target)) {
        setIsGuestMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    logout();
    setIsUserDropdownOpen(false);
  };

  return (
    <nav className="gadkot-nav overflow-visible">
      <div className="container mx-auto px-4">
        <div className="gadkot-nav__inner">
          {/* Logo */}
          <Link to="/" className="group flex shrink-0 items-center">
            <img
              src={GADKOT_LOGO}
              alt={t('nav.brandName')}
              className="navbar-logo h-12 w-auto min-h-[3rem] min-w-[8rem] object-contain object-left transition-transform duration-300 group-hover:scale-[1.02] sm:h-14 sm:min-w-[10rem] xl:h-[4.25rem] xl:min-w-[12rem]"
              decoding="async"
            />
          </Link>

          {/* Desktop Navigation — heritage rail */}
          <div className="hidden lg:flex min-w-0 flex-1 items-center justify-center overflow-visible px-1">
            <div className="gadkot-nav__rail max-w-full">
              <Link to="/" className={navItemClass(isActive('/'))}>
                <Home className="w-4 h-4 shrink-0" />
                <span>{t('nav.home')}</span>
              </Link>
              
              {/* Explore Dropdown */}
              <div className="relative overflow-visible" ref={exploreDropdownRef}>
                  <button
                  type="button"
                  onClick={() => setIsExploreDropdownOpen(!isExploreDropdownOpen)}
                  className={navItemClass(isExploreActive())}
                >
                  <Compass className="w-4 h-4 shrink-0" />
                  <span>{t('nav.explore')}</span>
                  <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${isExploreDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isExploreDropdownOpen && (
                  <div className="absolute left-1/2 top-full z-[100] mt-3 flex -translate-x-[19rem] items-start">
                    {/* Main explore panel */}
                    <div
                      className={`gadkot-historic-menu flex w-[38rem] flex-col overflow-hidden ${
                        isWeaponsSubmenuOpen
                          ? 'rounded-l-2xl rounded-r-none border-r-0'
                          : 'rounded-2xl'
                      }`}
                    >
                      <div className="gadkot-historic-menu__header">
                        <span className="gadkot-historic-menu__crest"><Compass className="h-5 w-5" /></span>
                        <div>
                          <p className="font-marathi-display font-bold text-[#fff3d6]">{t('nav.exploreGallery')}</p>
                          <p className="font-marathi text-[11px] text-[#d9c6a2]">{t('nav.exploreGalleryDesc')}</p>
                        </div>
                      </div>
                      <div className="gadkot-historic-menu__explore-grid max-h-[min(23rem,58vh)] overflow-y-auto overscroll-contain p-2">
                        {exploreNavItems.map(({ to, labelKey, descKey, Icon, color, matchPrefix }) => (
                          <Link
                            key={to}
                            to={to}
                            className={`gadkot-historic-menu__item group ${
                              isActive(to) || (matchPrefix && location.pathname.startsWith(matchPrefix))
                                ? 'gadkot-historic-menu__item--active'
                                : ''
                            }`}
                            onClick={closeExploreMenus}
                          >
                            <span className="gadkot-historic-menu__icon"><Icon className={`h-5 w-5 ${color}`} /></span>
                            <div className="min-w-0 flex-1">
                              <p className="font-marathi font-semibold text-[#40291c]">{t(labelKey)}</p>
                              <p className="font-marathi text-xs text-[#765f4d]">{t(descKey)}</p>
                            </div>
                          </Link>
                        ))}
                      </div>

                      <div className="gadkot-historic-menu__weapons shrink-0">
                        <button
                          type="button"
                          onClick={() => setIsWeaponsSubmenuOpen((open) => !open)}
                          className={`gadkot-historic-menu__item group m-2 w-[calc(100%-1rem)] text-left ${
                            isWeaponsSubmenuOpen || location.pathname.startsWith('/weapons')
                              ? 'gadkot-historic-menu__item--active'
                              : ''
                          }`}
                          aria-expanded={isWeaponsSubmenuOpen}
                        >
                          <span className="gadkot-historic-menu__icon">
                            <weaponsNavItem.Icon className={`h-5 w-5 ${weaponsNavItem.color}`} />
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="font-marathi font-semibold text-[#40291c]">{t(weaponsNavItem.labelKey)}</p>
                            <p className="font-marathi text-xs text-[#765f4d]">{t(weaponsNavItem.descKey)}</p>
                          </div>
                          <ChevronRight
                            className={`h-4 w-4 shrink-0 transition-transform ${
                              isWeaponsSubmenuOpen ? 'translate-x-0.5 text-red-700' : 'text-[#9b762b]'
                            }`}
                          />
                        </button>
                      </div>
                    </div>

                    {/* Weapons flyout — sibling panel (not inside scroll, no clipping) */}
                    {isWeaponsSubmenuOpen && (
                      <div className="gadkot-historic-menu w-[17rem] shrink-0 overflow-hidden rounded-r-2xl rounded-l-none border-l-0">
                        <div className="gadkot-historic-menu__header min-h-[4.6rem]">
                          <span className="gadkot-historic-menu__crest"><Swords className="h-5 w-5" /></span>
                          <p className="font-marathi-display font-bold text-[#fff3d6]">
                            {t('nav.weapons')}
                          </p>
                        </div>
                        <div className="max-h-[min(23rem,58vh)] overflow-y-auto p-2">
                        <Link
                          to="/weapons"
                          className="gadkot-historic-menu__item"
                          onClick={closeExploreMenus}
                        >
                          <span className="gadkot-historic-menu__icon"><Swords className="h-5 w-5 text-red-700" /></span>
                          <div className="min-w-0 flex-1">
                            <p className="font-marathi text-sm font-semibold text-[#40291c]">{t('weapons.allWeapons')}</p>
                            <p className="font-marathi text-xs text-[#765f4d]">{t(weaponsNavItem.descKey)}</p>
                          </div>
                        </Link>
                        {weaponNavCategories.map(({ id, labelKey, Icon, color }) => (
                          <Link
                            key={id}
                            to={`/weapons/category/${id}`}
                            className="gadkot-historic-menu__item"
                            onClick={closeExploreMenus}
                          >
                            <span className="gadkot-historic-menu__icon"><Icon className={`h-5 w-5 ${color}`} /></span>
                            <p className="font-marathi text-sm font-semibold leading-snug text-[#40291c]">{t(labelKey)}</p>
                          </Link>
                        ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Tools Dropdown */}
              <div className="relative" ref={toolsDropdownRef}>
                <button
                  type="button"
                  onClick={() => setIsToolsDropdownOpen(!isToolsDropdownOpen)}
                  className={navItemClass(
                    isActive('/trek-planner') || isActive('/contributions')
                    || (featureVisibility.nav.tools.quiz && isActive('/quiz'))
                    || (featureVisibility.nav.tools.shortNotes && isActive('/short-notes'))
                    || (featureVisibility.nav.tools.learn && isActive('/learn'))
                    || (featureVisibility.nav.tools.badges && isActive('/badges'))
                  )}
                >
                  <Settings className="w-4 h-4 shrink-0" />
                  <span>{t('nav.tools')}</span>
                  <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${isToolsDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isToolsDropdownOpen && (
                  <div className="gadkot-tools-menu absolute left-1/2 top-full z-50 mt-3 w-[38rem] -translate-x-1/2 overflow-hidden rounded-2xl">
                    <div className="gadkot-tools-menu__lintel">
                      <span className="gadkot-tools-menu__seal" aria-hidden>
                        <ScrollText className="h-5 w-5" />
                      </span>
                      <div>
                        <p className="font-marathi-display text-base font-bold text-[#fff3d6]">
                          {t('nav.toolsGallery')}
                        </p>
                        <p className="font-marathi text-xs text-[#d9c6a2]">{t('nav.toolsGalleryDesc')}</p>
                      </div>
                      <span className="gadkot-tools-menu__rule" aria-hidden />
                    </div>
                    <div className="gadkot-tools-menu__grid">
                    {toolsNavItems.map(({ to, labelKey, descKey, Icon, color, matchPrefix }) => (
                      <Link
                        key={to}
                        to={to}
                        className={`gadkot-tools-menu__item group ${
                          isActive(to) || (matchPrefix && location.pathname.startsWith(matchPrefix))
                            ? 'gadkot-tools-menu__item--active'
                            : ''
                        }`}
                        onClick={() => setIsToolsDropdownOpen(false)}
                      >
                        <span className="gadkot-tools-menu__icon">
                          <Icon className={`h-5 w-5 ${color}`} />
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="font-marathi font-semibold text-[#40291c]">{t(labelKey)}</p>
                          <p className="font-marathi mt-0.5 text-xs leading-relaxed text-[#765f4d]">{t(descKey)}</p>
                        </div>
                        <ChevronRight className="h-4 w-4 shrink-0 text-[#9b762b] opacity-0 transition-all group-hover:translate-x-0.5 group-hover:opacity-100" />
                      </Link>
                    ))}
                    {optionalToolsNavItems.map(({ featureKey, to, labelKey, descKey, Icon, color }) =>
                      featureVisibility.nav.tools[featureKey] ? (
                        <Link
                          key={to}
                          to={to}
                          className={`gadkot-tools-menu__item group ${isActive(to) ? 'gadkot-tools-menu__item--active' : ''}`}
                          onClick={() => setIsToolsDropdownOpen(false)}
                        >
                          <span className="gadkot-tools-menu__icon">
                            <Icon className={`h-5 w-5 ${color}`} />
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="font-marathi font-semibold text-[#40291c]">{t(labelKey)}</p>
                            <p className="font-marathi mt-0.5 text-xs leading-relaxed text-[#765f4d]">{t(descKey)}</p>
                          </div>
                          <ChevronRight className="h-4 w-4 shrink-0 text-[#9b762b] opacity-0 transition-all group-hover:translate-x-0.5 group-hover:opacity-100" />
                        </Link>
                      ) : null
                    )}
                    </div>
                    <div className="gadkot-tools-menu__footer font-marathi">{t('nav.toolsGalleryFooter')}</div>
                  </div>
                )}
              </div>

              {/* Organizations & Groups Dropdown */}
              <div className="relative" ref={organizationsDropdownRef}>
                <button
                  type="button"
                  onClick={() => setIsOrganizationsDropdownOpen(!isOrganizationsDropdownOpen)}
                  className={navItemClass(isOrganizationsActive())}
                >
                  <Users className="w-4 h-4 shrink-0" />
                  <span>{t('nav.organizations')}</span>
                  <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${isOrganizationsDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {isOrganizationsDropdownOpen && (
                  <div className="gadkot-historic-menu absolute left-1/2 top-full z-50 mt-3 w-[36rem] -translate-x-1/2 overflow-hidden rounded-2xl">
                    <div className="gadkot-historic-menu__header">
                      <span className="gadkot-historic-menu__crest"><Users className="h-5 w-5" /></span>
                      <div>
                        <p className="font-marathi-display font-bold text-[#fff3d6]">{t('nav.organizationsGallery')}</p>
                        <p className="font-marathi text-[11px] text-[#d9c6a2]">{t('nav.organizationsGalleryDesc')}</p>
                      </div>
                    </div>
                    <div className="gadkot-historic-menu__org-grid max-h-[min(28rem,68vh)] overflow-y-auto p-2">
                    {organizationNavCategories.map(({ code, labelKey, Icon, color }) => (
                      <Link
                        key={code}
                        to={`/organizations/category/${code}`}
                        className={`gadkot-historic-menu__item ${
                          location.pathname === `/organizations/category/${code}`
                            ? 'gadkot-historic-menu__item--active'
                            : ''
                        }`}
                        onClick={() => setIsOrganizationsDropdownOpen(false)}
                      >
                        <span className="gadkot-historic-menu__icon"><Icon className={`h-5 w-5 ${color}`} /></span>
                        <p className="font-marathi text-sm font-semibold leading-snug text-[#40291c]">{t(labelKey)}</p>
                      </Link>
                    ))}
                    </div>
                    <div className="gadkot-historic-menu__footer font-marathi">{t('nav.organizationsGalleryFooter')}</div>
                  </div>
                )}
              </div>

              <Link
                to="/search"
                className={navItemClass(isActive('/search'))}
                title={t('nav.search')}
              >
                <Search className="w-4 h-4 shrink-0" />
                <span className="hidden xl:inline">{t('nav.search')}</span>
              </Link>
            </div>
          </div>

          {/* Right Section */}
          <div className="flex shrink-0 items-center gap-2">
            {/* Language Selector */}
            <LanguageSelector />
            
            {/* User Actions */}
            {isAuthenticated ? (
              <div className="relative" ref={userDropdownRef}>
                <button
                  onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                  className="gadkot-nav__user-btn"
                >
                  <img
                    src={user?.avatar || `https://ui-avatars.com/api/?name=${user?.name || 'User'}&background=random`}
                    alt={user?.name || 'User'}
                    className="w-8 h-8 rounded-full"
                  />
                  <ChevronDown className={`w-4 h-4 transition-transform ${isUserDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isUserDropdownOpen && (
                  <div className="gadkot-nav__user-menu absolute top-full right-0 z-50 mt-2 w-64 rounded-xl py-2">
                    <div className="gadkot-nav__user-menu-header px-4 py-3">
                      <p className="font-medium text-gray-800">{user?.name || 'User'}</p>
                      <p className="text-sm text-gray-500">{user?.email || 'user@example.com'}</p>
                    </div>
                    
                    <Link
                      to="/profile"
                      className="gadkot-nav__dropdown-item flex items-center space-x-3 px-4 py-3"
                      onClick={() => setIsUserDropdownOpen(false)}
                    >
                      <User className="w-5 h-5 text-gray-500" />
                      <span className="text-gray-800">{t('ui.profile')}</span>
                    </Link>
                    
                    <Link
                      to="/wishlist"
                      className="gadkot-nav__dropdown-item flex items-center space-x-3 px-4 py-3"
                      onClick={() => setIsUserDropdownOpen(false)}
                    >
                      <Heart className="w-5 h-5 text-gray-500" />
                      <span className="text-gray-800">{t('nav.wishlist')}</span>
                    </Link>
                    
                    <Link
                      to="/planner"
                      className="gadkot-nav__dropdown-item flex items-center space-x-3 px-4 py-3"
                      onClick={() => setIsUserDropdownOpen(false)}
                    >
                      <Calendar className="w-5 h-5 text-gray-500" />
                      <span className="text-gray-800">{t('nav.visitPlanner')}</span>
                    </Link>

                    {orgRepUser && (
                      <Link
                        to="/organizations/manage"
                        className="gadkot-nav__dropdown-item flex items-center space-x-3 px-4 py-3"
                        onClick={() => setIsUserDropdownOpen(false)}
                      >
                        <Users className="w-5 h-5 text-gray-500" />
                        <span className="text-gray-800">{t('nav.manageOrganization')}</span>
                      </Link>
                    )}
                    
                    {adminUser && (
                      <>
                        <div className="gadkot-nav__user-menu-divider my-2" />
                        <Link
                          to="/admin"
                          className="gadkot-nav__dropdown-item flex items-center space-x-3 px-4 py-3"
                          onClick={() => setIsUserDropdownOpen(false)}
                        >
                          <Users className="w-5 h-5 text-gray-500" />
                          <span className="text-gray-800">{t('nav.adminDashboard')}</span>
                        </Link>
                        <Link
                          to="/admin/upload"
                          className="gadkot-nav__dropdown-item flex items-center space-x-3 px-4 py-3"
                          onClick={() => setIsUserDropdownOpen(false)}
                        >
                          <Settings className="w-5 h-5 text-gray-500" />
                          <span className="text-gray-800">{t('nav.uploadManage')}</span>
                        </Link>
                      </>
                    )}
                    
                    <div className="gadkot-nav__user-menu-divider my-2" />
                    <button
                      onClick={handleLogout}
                      className="gadkot-nav__dropdown-item flex w-full items-center space-x-3 px-4 py-3 text-left"
                    >
                      <LogOut className="w-5 h-5 text-red-500" />
                      <span className="text-red-600">{t('ui.logout')}</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="relative" ref={guestMenuRef}>
                <button
                  type="button"
                  onClick={() => setIsGuestMenuOpen((open) => !open)}
                  className="gadkot-nav__signin"
                  aria-expanded={isGuestMenuOpen}
                  aria-haspopup="menu"
                >
                  <User className="h-4 w-4 shrink-0" />
                  <span className="hidden sm:inline font-marathi">{t('nav.account')}</span>
                  <ChevronDown
                    className={`h-4 w-4 shrink-0 transition-transform ${isGuestMenuOpen ? 'rotate-180' : ''}`}
                  />
                </button>

                {isGuestMenuOpen && (
                  <div
                    className="gadkot-nav__dropdown gadkot-nav__guest-menu absolute top-full right-0 z-50 mt-2 w-72 rounded-xl py-2"
                    role="menu"
                  >
                    <p className="gadkot-nav__guest-hint font-marathi px-4 py-2 text-xs text-stone-600">
                      {t('nav.accountMenuHint')}
                    </p>
                    <button
                      type="button"
                      role="menuitem"
                      className="gadkot-nav__guest-item w-full text-left"
                      onClick={() => {
                        setIsGuestMenuOpen(false);
                        setShowAuthModal(true);
                      }}
                    >
                      <span className="font-marathi font-semibold text-[#2a1810]">{t('auth.signIn')}</span>
                      <span className="font-marathi mt-0.5 block text-xs text-stone-500">
                        {t('auth.signInMenuDesc')}
                      </span>
                    </button>
                    <Link
                      to="/register"
                      role="menuitem"
                      className="gadkot-nav__guest-item block"
                      onClick={() => setIsGuestMenuOpen(false)}
                    >
                      <span className="font-marathi font-semibold text-[#2a1810]">{t('auth.registerLink')}</span>
                      <span className="font-marathi mt-0.5 block text-xs text-stone-500">
                        {t('auth.registerMenuDesc')}
                      </span>
                    </Link>
                  </div>
                )}
              </div>
            )}
            
            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="gadkot-nav__menu-btn lg:hidden"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="gadkot-nav__mobile lg:hidden py-4">
            <div className="space-y-2">
              <Link
                to="/"
                className={`gadkot-nav__mobile-link ${isActive('/') ? 'gadkot-nav__mobile-link--active' : ''}`}
                onClick={() => setIsOpen(false)}
              >
                <Home className="w-5 h-5" />
                <span className="font-marathi font-medium">{t('nav.home')}</span>
              </Link>
              
              <div className="space-y-1">
                <div className="gadkot-nav__mobile-section">{t('nav.explore')}</div>
                {exploreNavItems.map(({ to, labelKey, Icon, color, matchPrefix }) => {
                  const active = matchPrefix
                    ? location.pathname === to || location.pathname.startsWith(matchPrefix)
                    : isActive(to);
                  return (
                    <Link
                      key={to}
                      to={to}
                      className={`gadkot-nav__mobile-link ${active ? 'gadkot-nav__mobile-link--active' : ''}`}
                      onClick={() => setIsOpen(false)}
                    >
                      <Icon className={`w-5 h-5 shrink-0 ${color}`} />
                      <span className="font-marathi font-medium">{t(labelKey)}</span>
                    </Link>
                  );
                })}
                <button
                  type="button"
                  onClick={() => setIsMobileWeaponsOpen((open) => !open)}
                  className={mobileLinkClass(location.pathname.startsWith('/weapons'))}
                  aria-expanded={isMobileWeaponsOpen}
                >
                  <weaponsNavItem.Icon className={`w-5 h-5 shrink-0 ${weaponsNavItem.color}`} />
                  <span className="font-marathi font-medium flex-1 text-left">{t(weaponsNavItem.labelKey)}</span>
                  <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${isMobileWeaponsOpen ? 'rotate-180' : ''}`} />
                </button>
                {isMobileWeaponsOpen && (
                  <>
                    <Link
                      to="/weapons"
                      className={mobileLinkClass(location.pathname === '/weapons', true)}
                      onClick={() => setIsOpen(false)}
                    >
                      <Swords className="w-4 h-4 text-red-600" />
                      <span className="font-marathi text-sm font-medium">{t('weapons.allWeapons')}</span>
                    </Link>
                    {weaponNavCategories.map(({ id, labelKey, Icon, color }) => (
                      <Link
                        key={id}
                        to={`/weapons/category/${id}`}
                        className={mobileLinkClass(location.pathname === `/weapons/category/${id}`, true)}
                        onClick={() => setIsOpen(false)}
                      >
                        <Icon className={`w-4 h-4 ${color}`} />
                        <span className="font-marathi text-sm">{t(labelKey)}</span>
                      </Link>
                    ))}
                  </>
                )}
                <Link
                  to="/geographic-explorer"
                  className={mobileLinkClass(isActive('/geographic-explorer'))}
                  onClick={() => setIsOpen(false)}
                >
                  <Map className="w-5 h-5" />
                  <span className="font-medium">{t('nav.geographicExplorer')}</span>
                </Link>
                <Link
                  to="/thematic-routes"
                  className={mobileLinkClass(isActive('/thematic-routes'))}
                  onClick={() => setIsOpen(false)}
                >
                  <Globe className="w-5 h-5" />
                  <span className="font-marathi font-medium">{t('nav.thematicRoutes')}</span>
                </Link>
              </div>
              
              <div className="space-y-1">
                <div className="gadkot-nav__mobile-section">{t('nav.tools')}</div>
                {toolsNavItems.map(({ to, labelKey, Icon, color }) => (
                  <Link
                    key={to}
                    to={to}
                    className={mobileLinkClass(isActive(to))}
                    onClick={() => setIsOpen(false)}
                  >
                    <Icon className={`w-5 h-5 shrink-0 ${color}`} />
                    <span className="font-medium">{t(labelKey)}</span>
                  </Link>
                ))}
                {optionalToolsNavItems.map(({ featureKey, to, labelKey, Icon, color }) =>
                  featureVisibility.nav.tools[featureKey] ? (
                    <Link
                      key={to}
                      to={to}
                      className={mobileLinkClass(isActive(to))}
                      onClick={() => setIsOpen(false)}
                    >
                      <Icon className={`w-5 h-5 shrink-0 ${color}`} />
                      <span className="font-medium">{t(labelKey)}</span>
                    </Link>
                  ) : null
                )}
                <Link
                  to="/search"
                  className={mobileLinkClass(isActive('/search'))}
                  onClick={() => setIsOpen(false)}
                >
                  <Search className="w-5 h-5" />
                  <span className="font-medium">{t('nav.search')}</span>
                </Link>
              </div>

              <div className="space-y-1">
                <div className="gadkot-nav__mobile-section">{t('nav.organizations')}</div>
                {organizationNavCategories.map(({ code, labelKey, Icon, color }) => (
                  <Link
                    key={code}
                    to={`/organizations/category/${code}`}
                    className={mobileLinkClass(location.pathname === `/organizations/category/${code}`)}
                    onClick={() => setIsOpen(false)}
                  >
                    <Icon className={`w-5 h-5 ${color}`} />
                    <span className="font-marathi font-medium text-sm">{t(labelKey)}</span>
                  </Link>
                ))}
              </div>

              {!isAuthenticated && (
                <div className="mt-4 space-y-1 border-t border-amber-200/50 pt-4">
                  <div className="gadkot-nav__mobile-section">{t('nav.account')}</div>
                  <button
                    type="button"
                    className={mobileLinkClass(false)}
                    onClick={() => {
                      setIsOpen(false);
                      setShowAuthModal(true);
                    }}
                  >
                    <User className="w-5 h-5" />
                    <span className="font-marathi font-medium">{t('auth.signIn')}</span>
                  </button>
                  <Link
                    to="/register"
                    className={mobileLinkClass(location.pathname === '/register', true)}
                    onClick={() => setIsOpen(false)}
                  >
                    <User className="w-5 h-5" />
                    <span className="font-marathi font-medium">{t('auth.registerLink')}</span>
                  </Link>
                </div>
              )}

              {isAuthenticated && orgRepUser && (
                <div className="mt-4 space-y-1 border-t border-amber-200/50 pt-4">
                  <div className="gadkot-nav__mobile-section">{t('nav.organizationSection')}</div>
                  <Link
                    to="/organizations/manage"
                    className={mobileLinkClass(location.pathname === '/organizations/manage')}
                    onClick={() => setIsOpen(false)}
                  >
                    <Users className="w-5 h-5" />
                    <span className="font-medium">{t('nav.manageOrganization')}</span>
                  </Link>
                </div>
              )}

              {isAuthenticated && adminUser && (
                <div className="mt-4 space-y-1 border-t border-amber-200/50 pt-4">
                  <div className="gadkot-nav__mobile-section">{t('nav.adminSection')}</div>
                  <Link
                    to="/admin"
                    className={mobileLinkClass(isActive('/admin'))}
                    onClick={() => setIsOpen(false)}
                  >
                    <Users className="w-5 h-5" />
                    <span className="font-medium">{t('nav.adminDashboard')}</span>
                  </Link>
                  <Link
                    to="/admin/upload"
                    className={mobileLinkClass(location.pathname.startsWith('/admin/upload'))}
                    onClick={() => setIsOpen(false)}
                  >
                    <Settings className="w-5 h-5" />
                    <span className="font-marathi font-medium">{t('nav.uploadManage')}</span>
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
      />
    </nav>
  );
};

export default Navbar;
